import { MapPin, Home, DollarSign, TrendingUp, Bookmark } from 'lucide-react';
import { Button } from './ui/button';

export const DealCard = ({ deal, isSaved, onToggleSave, onViewDetails }) => {
  const potentialProfit = deal.estimated_arv - deal.list_price - deal.estimated_repair_cost;
  const roi = ((potentialProfit / (deal.list_price + deal.estimated_repair_cost)) * 100).toFixed(1);
  const costPerSqft = deal.square_feet > 0 ? (deal.list_price / deal.square_feet).toFixed(0) : 0;
  const arvPerSqft = deal.square_feet > 0 ? (deal.estimated_arv / deal.square_feet).toFixed(0) : 0;

  return (
    <div data-testid={`deal-card-${deal.id}`} className="deal-card bg-zinc-950 border border-zinc-800 hover:border-green-500/50 flex flex-col md:flex-row overflow-hidden">
      <div className="md:w-1/3 h-48 md:h-auto relative">
        <img 
          src={deal.image_url} 
          alt={deal.address} 
          className="w-full h-full object-cover"
        />
        <div className="absolute top-2 right-2 flex gap-2">
          {deal.is_off_market && (
            <span className="px-2 py-1 bg-green-500/90 text-black text-xs font-mono uppercase font-bold">
              Off Market
            </span>
          )}
        </div>
      </div>

      <div className="md:w-2/3 p-4 flex flex-col justify-between">
        <div>
          <div className="flex justify-between items-start mb-2">
            <div>
              <h3 data-testid={`deal-address-${deal.id}`} className="text-lg font-mono font-bold mb-1">{deal.address}</h3>
              <p className="text-sm font-mono text-zinc-400 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {deal.city}, {deal.state}
              </p>
            </div>
            <Button 
              data-testid={`toggle-save-${deal.id}`}
              onClick={() => onToggleSave(deal.id)} 
              size="sm"
              className={`border rounded-none ${isSaved ? 'bg-green-600 border-green-600 text-black' : 'bg-transparent border-zinc-700 text-zinc-300'}`}
            >
              <Bookmark className={`w-4 h-4 ${isSaved ? 'fill-current' : ''}`} />
            </Button>
          </div>

          <div className="flex flex-wrap gap-1 mb-3">
            {deal.damage_type.slice(0, 2).map((damage, idx) => (
              <span key={idx} className="px-2 py-0.5 bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-mono uppercase">
                {damage}
              </span>
            ))}
            <span className="px-2 py-0.5 bg-zinc-800 border border-zinc-700 text-zinc-400 text-xs font-mono uppercase">
              {deal.condition}
            </span>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-3">
            <div>
              <p className="text-xs font-mono uppercase text-zinc-500">Type</p>
              <p className="text-sm font-mono font-bold">{deal.property_type}</p>
            </div>
            <div>
              <p className="text-xs font-mono uppercase text-zinc-500">Beds/Baths</p>
              <p className="text-sm font-mono font-bold">{deal.bedrooms}/{deal.bathrooms}</p>
            </div>
            <div>
              <p className="text-xs font-mono uppercase text-zinc-500">Sq Ft</p>
              <p className="text-sm font-mono font-bold">{deal.square_feet.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div>
          <div className="grid grid-cols-4 gap-2 mb-3 pb-3 border-b border-zinc-800">
            <div>
              <p className="text-xs font-mono uppercase text-zinc-500">Price</p>
              <p className="text-sm font-mono font-bold">${(deal.list_price / 1000).toFixed(0)}K</p>
            </div>
            <div>
              <p className="text-xs font-mono uppercase text-zinc-500">$/SqFt</p>
              <p className="text-sm font-mono font-bold text-yellow-500">${costPerSqft}</p>
            </div>
            <div>
              <p className="text-xs font-mono uppercase text-zinc-500">ARV</p>
              <p className="text-sm font-mono font-bold text-green-500">${(deal.estimated_arv / 1000).toFixed(0)}K</p>
            </div>
            <div>
              <p className="text-xs font-mono uppercase text-zinc-500">ARV/Ft</p>
              <p className="text-sm font-mono font-bold text-green-400">${arvPerSqft}</p>
            </div>
          </div>

          <Button 
            data-testid={`view-details-${deal.id}`}
            onClick={onViewDetails} 
            className="w-full bg-green-600 hover:bg-green-500 text-black font-mono uppercase tracking-wider rounded-none"
          >
            Analyze Deal
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DealCard;